import React, {useState} from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  TextInput,
  View,
  type StyleProp,
  type TextStyle,
  type TextInputProps,
  type ViewStyle,
} from 'react-native';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import {fonts} from '@/shared/ui/fonts';
import {Icon, type IconName} from '@/shared/ui/icons';
import {AppText} from '@/shared/ui/text';
import {controls, radii, useTheme, type Palette} from '@/shared/ui/theme';

/**
 * A labelled field. `secure` turns it into a password field with a reveal toggle —
 * typing a password blind on a phone keyboard is how people get locked out, so the
 * eye is not optional.
 *
 * Это `Input` дизайн-системы SamVision (`ui-kit/components/form`): поле ЗАЛИТО
 * `surface-base` — на полтона глубже панели, на которой стоит, — с рамкой
 * `border-default` и радиусом 2pt. Поле отличается от карточки заливкой, а не
 * только линией, поэтому рамке не нужно держать 3:1. Наведение — `border-strong`;
 * фокус — акцентная рамка, двойная (внешняя плюс внутренняя 1pt): широкое кольцо в
 * плотной форме налезало бы на соседнее поле, а одной смены цвета мало на тёмном
 * фоне. Ошибка старше фокуса: поле, которое исправляют, держит красную рамку и с
 * каретом в нём.
 *
 * Подпись — 12px вторичной краской над полем: она называет поле, а не соперничает с
 * тем, что в нём.
 *
 * Две ступени высоты — `md` (34, `--control-height-lg` рабочей станции) под
 * указатель и `lg` (44) под палец; по умолчанию выбирается по ширине окна.
 *
 * THE TEXT is 15pt on the phone, not the ramp's 13. Mobile Safari zooms the viewport
 * on focusing any field under 16px and does not zoom back out; 15 is as close to the
 * ramp as the browser lets this get without the page jumping under the user's thumb.
 *
 * No user-facing string is baked in — `label`, `error` and the toggle's accessibility
 * labels all arrive as props, so i18n resolves at the call site.
 */
export type AppInputProps = Omit<TextInputProps, 'secureTextEntry' | 'style'> & {
  label?: string;
  value: string;
  onChangeText: (text: string) => void;
  secure?: boolean;
  error?: string;
  /** A quiet line under the field explaining the rule (min length, format). */
  hint?: string;
  /** A glyph inside the field, before the text — `search` on a search bar. */
  icon?: IconName;
  /**
   * A control drawn inside the field, after the text — «Сгенерировать» on an
   * employee number, a clear button on a filter.
   *
   * Inside rather than under the field on purpose: a verb that fills THIS field
   * has to sit in it, or it reads as the next question on the form. Mutually
   * exclusive with `secure` in practice — a password field's own reveal toggle
   * already owns that corner.
   */
  trailing?: React.ReactNode;
  /**
   * `lg` (44pt) — поле, в которое попадают пальцем. `md` (36pt, shadcn `h-9`) — поле
   * под указатель. По умолчанию выбирается по ширине окна: на телефоне `lg`, на
   * широком окне `md` — форма из восьми 44pt полей на мониторе была в полтора раза
   * длиннее, чем нужно, ради пальца, которого там нет.
   */
  size?: 'md' | 'lg';
  style?: StyleProp<ViewStyle>;
  /** Accessibility label for the reveal button while the password is hidden. */
  showPasswordLabel?: string;
  /** Accessibility label for the reveal button while the password is shown. */
  hidePasswordLabel?: string;
};

const styles = StyleSheet.create({
  field: {gap: 3},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: radii.sm,
    paddingLeft: 8,
  },
  /* Внутренняя линия фокуса — `inset 0 0 0 1px` ui-kit. `pointerEvents` off so it
     never eats a tap. */
  ring: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    borderWidth: 1,
    borderRadius: radii.sm,
  },
  input: {
    flex: 1,
    minHeight: controls.lg - 2,
    paddingHorizontal: 0,
    paddingVertical: 10,
    marginLeft: 0,
    fontSize: 15,
    // A TextInput does not inherit AppText's family: without this, what the user
    // types renders in the system font while every label around it is Play.
    fontFamily: fonts.sans.regular,
  },
  inputMd: {minHeight: controls.md - 2, paddingVertical: 4, fontSize: 13},
  leading: {marginRight: 6},
  // Sits on the field's own right padding, so the control's edge lines up with
  // the text of every other field in the form rather than 12pt short of it.
  trailing: {marginLeft: 8, marginRight: 6},
  // Во всю высоту поля: 44pt под палец на телефоне, 34 под указатель на мониторе.
  toggle: {
    width: 40,
    alignSelf: 'stretch',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

/**
 * The browser paints its own focus ring on a focused <input>, which lands on top of —
 * and disagrees with — the teal ring below. `outlineStyle` is a react-native-web-only
 * style key, hence the cast and the platform guard.
 */
const NO_BROWSER_OUTLINE =
  Platform.OS === 'web' ? ({outlineStyle: 'none'} as unknown as TextStyle) : null;

export function AppInput({
  label,
  value,
  onChangeText,
  secure = false,
  error,
  hint,
  icon,
  trailing,
  size,
  style,
  showPasswordLabel = 'Show password',
  hidePasswordLabel = 'Hide password',
  onFocus,
  onBlur,
  ...rest
}: AppInputProps) {
  const p: Palette = useTheme();
  const {isDesktop} = useBreakpoint();
  const sizeRung = size ?? (isDesktop ? 'md' : 'lg');
  const [revealed, setRevealed] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);

  // Ошибка — коралловый `destructive`, тот же, что у отказа в проходе: один цвет
  // тревоги, одно значение. Error outranks focus: a field you are fixing must keep
  // showing that it is wrong.
  const accent = error ? p.destructive : p.ring;
  const borderColor = error
    ? p.destructive
    : focused
    ? p.primary
    : hovered
    ? p.borderStrong
    : p.input;
  // `background` — поле утоплено на полтона глубже панели, на которой стоит.
  const background = p.background;

  return (
    <View style={[styles.field, style]}>
      {label ? <AppText variant="label">{label}</AppText> : null}

      <View
        style={[styles.row, {backgroundColor: background, borderColor}]}
        // `onMouseEnter`/`onMouseLeave` — только веб; на телефоне их никто не зовёт.
        {...({
          onMouseEnter: () => setHovered(true),
          onMouseLeave: () => setHovered(false),
        } as object)}>
        {focused ? (
          <View pointerEvents="none" style={[styles.ring, {borderColor: accent}]} />
        ) : null}
        {icon ? (
          <View style={styles.leading}>
            <Icon name={icon} size={sizeRung === 'md' ? 15 : 18} color={p.mutedForeground} />
          </View>
        ) : null}

        <TextInput
          {...rest}
          value={value}
          onChangeText={onChangeText}
          secureTextEntry={secure && !revealed}
          accessibilityLabel={label}
          placeholderTextColor={p.mutedForeground}
          selectionColor={p.primary}
          onFocus={e => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={e => {
            setFocused(false);
            onBlur?.(e);
          }}
          style={[
            styles.input,
            sizeRung === 'md' && styles.inputMd,
            {color: p.foreground},
            !secure && !trailing && {paddingRight: 8},
            NO_BROWSER_OUTLINE,
          ]}
        />

        {trailing ? <View style={styles.trailing}>{trailing}</View> : null}

        {secure ? (
          <Pressable
            testID="password-toggle"
            onPress={() => setRevealed(v => !v)}
            accessibilityRole="button"
            accessibilityState={{expanded: revealed}}
            accessibilityLabel={revealed ? hidePasswordLabel : showPasswordLabel}
            // The eye is an aid, not a stop on the way to the submit button.
            focusable={false}
            hitSlop={6}
            style={styles.toggle}>
            <Icon name={revealed ? 'eyeOff' : 'eye'} size={18} color={p.mutedForeground} />
          </Pressable>
        ) : null}
      </View>

      {error ? (
        <AppText variant="caption" color={p.destructiveInk}>
          {error}
        </AppText>
      ) : hint ? (
        <AppText variant="caption">{hint}</AppText>
      ) : null}
    </View>
  );
}
